import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { useAuth } from '@/lib/auth/AuthProvider'
import { reportError } from '@/lib/errors'
import { toast } from '@/lib/toast'

import View from './UserMenu.view'

export type ControllerProps = {
  className?: string
}

export const Controller = memo(function UserMenuController({ className }: ControllerProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const closeWhenLeaving = useCallback((nextTarget: EventTarget | null) => {
    if (!(nextTarget instanceof Node) || !rootRef.current?.contains(nextTarget)) setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      closeWhenLeaving(event.target)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointerDown)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
  }, [closeWhenLeaving, isOpen])

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true)
    try {
      await logout()
      await navigate({ to: '/login' })
      // Sign-out is a client-side navigation, so drop every cached response;
      // otherwise the next account to sign in on this tab briefly sees the
      // previous user's history, settings, and admin data.
      queryClient.clear()
    } catch (error) {
      reportError(error, { operation: 'Sign out' })
      toast.error('Could not sign out', { description: 'Your session could not be closed. Try signing out again.' })
    } finally {
      setIsSigningOut(false)
      setIsOpen(false)
    }
  }, [logout, navigate, queryClient])

  if (!user) return null

  return (
    <View
      rootRef={rootRef}
      className={className}
      user={user}
      isOpen={isOpen}
      isSigningOut={isSigningOut}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={(event) => closeWhenLeaving(event.relatedTarget)}
      onBlur={(event) => closeWhenLeaving(event.relatedTarget)}
      onToggle={() => setIsOpen((open) => !open)}
      onEscape={() => setIsOpen(false)}
      onProfile={(event) => {
        if (isSigningOut) {
          event.preventDefault()
          return
        }
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        setIsOpen(false)
        void navigate({ to: '/profile' })
      }}
      onSignOut={() => void handleSignOut()}
    />
  )
})

Controller.displayName = 'UserMenu_Controller'
